/**
 * Every non-2xx response and every unusable response body becomes one of these.
 *
 * It carries the stable machine code — never prose (docs/011-i18n.md). The UI
 * renders it with `errorMessage(t, error.code)`. `message` exists only because
 * `Error` has one; it is the code, and it is for logs, never for a screen.
 */
export class ApiError extends Error {
    code;
    statusCode;
    details;
    /**
     * The server's `x-request-id` (`REQUEST_ID_HEADER`,
     * apps/api/src/lib/request-context.ts). The server stamps that same id on every
     * log line it writes for the request, so this is what turns a report of "it
     * broke" into a specific log line an operator can pull up.
     *
     * REQUIRED, NOT OPTIONAL, AND DELIBERATELY SO. It was optional until GitHub
     * issue #57, and the cost of that was sixteen write-path error blocks that each
     * had to *remember* to render it, and none did — every one of them had the id in
     * hand the whole time. A shared error control cannot force a caller to supply
     * diagnostic detail the caller is allowed not to have, so the type carries the
     * guarantee instead: construct an `ApiError` and you have an id to show.
     *
     * `toApiError` in transport.ts mints a synthetic `client-*` id when a response
     * arrives without one, so the field is populated even against a server that did
     * not stamp the header. A failure that never produced a response at all is NOT
     * an `ApiError` — the transport rethrows the raw fetch error and reports it as
     * the `'network'` kind on `SdkErrorEvent` — so there is no case here that has
     * nothing to put in this field.
     */
    requestId;
    /**
     * DEV-ONLY server debug detail (`ErrorEnvelope.debug`, `@instanexus/contracts`) —
     * the real error's `name`, `message`, and `stack`, present only when the API's own
     * resolved `NODE_ENV` was exactly `'development'` (`apps/api/src/lib/error-middleware.ts`).
     * Undefined in every other environment, including production and test — never
     * gated on anything client-side, so a browser cannot turn this on for itself.
     */
    debug;
    /**
     * The permission a refused request required, when the server named it on a 403
     * (`ApiErrorContext.permission`). Optional twice over: the API does not send it today
     * (docs/008 S8 — see ADR 0034's amendment), and a client must never assume a field a
     * server may not have shipped yet.
     */
    permission;
    /**
     * The request this error answered — method and origin-relative path with any query
     * string already dropped, stamped by the transport. It exists so the one console seam
     * (`reportPermissionDenied`, ./report-error.ts) can name WHAT was refused from the
     * error alone; a TanStack Query `onError` never sees the request.
     */
    request;
    constructor(code, statusCode, details, requestId, debug, context) {
        super(code);
        this.name = 'ApiError';
        this.code = code;
        this.statusCode = statusCode;
        this.details = details;
        this.requestId = requestId;
        this.debug = debug;
        this.permission = context?.permission;
        this.request = context?.request;
    }
}
export function isApiError(value) {
    return value instanceof ApiError;
}
/**
 * `fetch` rejects with a `TypeError` when no HTTP answer arrives, worded per engine: Chromium
 * "Failed to fetch", Firefox "NetworkError when attempting to fetch resource.", WebKit "Load
 * failed". A `TypeError` from a bug in our own code must not be dressed up as "offline".
 */
const NETWORK_FAILURE_MESSAGE = /failed to fetch|networkerror|load failed|network request failed/i;
function isNetworkFailure(error) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false)
        return true;
    return error instanceof TypeError && NETWORK_FAILURE_MESSAGE.test(error.message);
}
/**
 * Classify whatever a failed request threw. An `ApiError` reached the server and carries its
 * code and status; a network failure never got an HTTP answer; anything else is a client-side
 * fault and stays `unexpected`.
 */
export function classifyApiError(error) {
    if (isApiError(error)) {
        if (error.code === 'FORBIDDEN')
            return 'forbidden';
        if (error.code === 'NOT_FOUND')
            return 'notFound';
        if (error.statusCode === 401)
            return 'unauthenticated';
        if (error.statusCode === 409)
            return 'conflict';
        if (error.statusCode === 400 || error.statusCode === 422)
            return 'validation';
        return 'unexpected';
    }
    return isNetworkFailure(error) ? 'offline' : 'unexpected';
}
/**
 * `classifyApiError` narrowed to the four kinds a query error state renders. Validation,
 * conflict and unauthenticated failures keep their code-specific message, which the
 * `unexpected` branch of a `QueryErrorState` resolves via `errorMessage(t, code)`.
 */
export function errorKindOf(error) {
    const kind = classifyApiError(error);
    return kind === 'forbidden' || kind === 'notFound' || kind === 'offline' ? kind : 'unexpected';
}
//# sourceMappingURL=api-error.js.map